import { z } from 'zod';

import { DecisionMode, ModelsConfig, scannerProfiles, shared } from '@tig-dpqa-cloud/contract-backend-common';

// TODO according to ticket the following things need to be added, but no data is available for now:
// tissue (specimen) type, organ
export const AnonymizedMetadataSchema = z.object({
  schema_version: z.literal('1.0.4').prefault('1.0.4').describe('The version of the anonymized metadata format.'),
  issues: z.array(
    z.object({
      type: shared.SlideScanIssue,
      status: shared.SlideScanVerifiedIssueStatus,
    }),
  ),
  annotator: z.string().optional().describe('Hashed identifier of the annotator (i.e., whomever verified the slide)'),
  status: z.enum(['SUCCESS', 'FAILED']).describe('The state of the AI processing'),
  files: z.array(z.string()).describe('List of files in the accompanying archive'),
  archive: z.string().describe('Name of the archive containing the anonymized files'),
  staining: z.string().optional(),
  case_name: z.string().optional(),
  slide_name: z.string().optional(),
  is_phi_detected: z.boolean(),
  general_qc_status: shared.SlideScanGeneralQcStatus,
  rescan_requested: z.boolean(),
  body_part: z.string().optional(),
  comment: z.string().optional(),
  custom_fields: z.record(z.string(), z.object({ display_name: z.string(), value: z.string() })).optional(),
  models_config: ModelsConfig.optional(),
  models_config_creator: z.string().optional().describe('Hashed identifier of the user who created the models config'),
  models_config_created_at: z.date().transform((d) => d.toISOString()).optional().describe('ISO timestamp of when the models config was created'),
});

const InterpretationThreshold_v1_0_3 = z.object({
  lowerThreshold: z.number().nonnegative().describe('Maximum value (included) to consider an issue of level "S" (small)'),
  upperThreshold: z.number().nonnegative().describe('Maximum value (included) to consider an issue of level "M" (medium)'),
  automaticRescan: z.boolean().describe('Automatically rescan if above upper threshold - classified as "L" (large)'),
  decisionMode: DecisionMode.prefault('AUTOMATIC').describe('Strategy for handling automatic quality decisions'),
});

const Interpretation_v1_0_3 = z.object({
  fallback: InterpretationThreshold_v1_0_3.pick({ decisionMode: true }).describe('Fallback decision mode').optional(),
  blur: InterpretationThreshold_v1_0_3,
  missingTissue: InterpretationThreshold_v1_0_3,
  misalignedCoverSlip: InterpretationThreshold_v1_0_3,
  cutOffTissue: InterpretationThreshold_v1_0_3,
});

const ModelsConfig_v1_0_3 = z.object({
  schema_version: z.string().default('1.0.1').describe('Version of the models configuration schema'),
  detection: z.object({
    scannerProfiles: scannerProfiles.optional(),
  }),
  interpretation: Interpretation_v1_0_3,
});

/**
 * Previous version of the schema (v1.0.3, as of git tag v1.1.1)
 * Not used within the application,
 * only kept to show it in the swagger docs so that research can easily compare versions.
 *
 * Differences from v1.0.4:
 * - interpretation had a fallback
 * - greenDecisionMode and redDecisionMode were in a single decisionMode field
 *
 * Subschema values are inlined to preserve the exact v1.0.3 contract.
 */
export const AnonymizedMetadataSchema_v1_0_3 = z.object({
  schema_version: z.literal('1.0.3').prefault('1.0.3').describe('The version of the anonymized metadata format.'),
  issues: z.array(
    z.object({
      type: shared.SlideScanIssue,
      status: shared.SlideScanVerifiedIssueStatus,
    }),
  ),
  annotator: z.string().optional().describe('Hashed identifier of the annotator (i.e., whomever verified the slide)'),
  status: z.enum(['SUCCESS', 'FAILED']).describe('The state of the AI processing'),
  files: z.array(z.string()).describe('List of files in the accompanying archive'),
  archive: z.string().describe('Name of the archive containing the anonymized files'),
  staining: z.string().optional(),
  case_name: z.string().optional(),
  slide_name: z.string().optional(),
  is_phi_detected: z.boolean(),
  general_qc_status: shared.SlideScanGeneralQcStatus,
  rescan_requested: z.boolean(),
  body_part: z.string().optional(),
  comment: z.string().optional(),
  custom_fields: z.record(z.string(), z.object({ display_name: z.string(), value: z.string() })).optional(),
  models_config: ModelsConfig_v1_0_3.optional(),
  models_config_creator: z.string().optional().describe('Hashed identifier of the user who created the models config'),
  models_config_created_at: z.date().transform((d) => d.toISOString()).optional().describe('ISO timestamp of when the models config was created'),
});

/**
 * Previous version of the schema (v1.0.2, as of git tag v1.1.1)
 * Not used within the application,
 * only kept to show it in the swagger docs so that research can easily compare versions.
 *
 * Differences from v1.0.3:
 * - issues.type: did not have MARKER_ON_TOP_OF_TISSUE; had MARKER instead
 * - issues.status: did not have TRUE_POSITIVE, TRUE_NEGATIVE
 * - models_config: interpretation did not have fallback or markerOnTopOfTissue; thresholds did not have decisionMode
 * - did not have models_config_creator, models_config_created_at
 *
 * Subschema values are inlined to preserve the exact v1.0.2 contract.
 */
const SlideScanIssue_v1_0_2 = z.enum([
  'TISSUE_NOT_DETECTED',
  'OUT_OF_FOCUS_REGION',
  'FOLDS',
  'MISALIGNED_COVER_SLIP',
  'CUT_OFF_TISSUE',
  'MARKER',
  'STRIPES',
  'MISALIGNED_IMAGE_STITCHING',
  'AIR_BUBBLES',
  'THICKNESS_VARIATION',
  'STAINING_INHOMOGENEITY',
  'FOREIGN_MATERIAL',
  'OTHER',
  'TISSUE_OUTSIDE_MAXIMUM_SCANNING_AREA',
  'TISSUE_OUTSIDE_COVER_AREA',
]);

const SlideScanVerifiedIssueStatus_v1_0_2 = z.enum([
  'FALSE_POSITIVE',
  'FALSE_NEGATIVE',
  'ACCEPTED',
  'USER_ONLY',
  'IGNORED',
]);

const InterpretationThreshold_v1_0_2 = z.object({
  lowerThreshold: z.number().nonnegative().describe('Maximum value (included) to consider an issue of level "S" (small)'),
  upperThreshold: z.number().nonnegative().describe('Maximum value (included) to consider an issue of level "M" (medium)'),
  automaticRescan: z.boolean().describe('Automatically rescan if above upper threshold - classified as "L" (large)'),
});

const Interpretation_v1_0_2 = z.object({
  blur: InterpretationThreshold_v1_0_2,
  missingTissue: InterpretationThreshold_v1_0_2,
  misalignedCoverSlip: InterpretationThreshold_v1_0_2,
  cutOffTissue: InterpretationThreshold_v1_0_2,
});

const ModelsConfig_v1_0_2 = z.object({
  schema_version: z.string().default('1.0.1').describe('Version of the models configuration schema'),
  detection: z.object({
    scannerProfiles: scannerProfiles.optional(),
  }),
  interpretation: Interpretation_v1_0_2,
});

export const AnonymizedMetadataSchema_v1_0_2 = z.object({
  schema_version: z.literal('1.0.2').prefault('1.0.2').describe('The version of the anonymized metadata format.'),
  issues: z.array(
    z.object({
      type: SlideScanIssue_v1_0_2,
      status: SlideScanVerifiedIssueStatus_v1_0_2,
    }),
  ),
  annotator: z.string().optional().describe('Hashed identifier of the annotator (i.e., whomever verified the slide)'),
  status: z.enum(['SUCCESS', 'FAILED']).describe('The state of the AI processing'),
  files: z.array(z.string()).describe('List of files in the accompanying archive'),
  archive: z.string().describe('Name of the archive containing the anonymized files'),
  staining: z.string().optional(),
  case_name: z.string().optional(),
  slide_name: z.string().optional(),
  is_phi_detected: z.boolean(),
  general_qc_status: z.enum(['NOT_SET', 'GOOD', 'BAD']),
  rescan_requested: z.boolean(),
  body_part: z.string().optional(),
  comment: z.string().optional(),
  custom_fields: z.record(z.string(), z.object({ display_name: z.string(), value: z.string() })).optional(),
  models_config: ModelsConfig_v1_0_2.optional(),
});

export type AnonymizedMetadataInputType = z.input<typeof AnonymizedMetadataSchema>;
export type AnonymizedMetadataType = z.infer<typeof AnonymizedMetadataSchema>;

export const ExtractedMetadataSchema = z.object({
  schema_version: z.literal('1.0.0').prefault('1.0.0').describe('The version of the extracted metadata format.'),
  scanned_area: z.object({
    y_start: z.number().describe('The starting Y coordinate of the scanned area.'),
    x_start: z.number().describe('The starting X coordinate of the scanned area.'),
    y_stop: z.number().describe('The stopping Y coordinate of the scanned area.'),
    x_stop: z.number().describe('The stopping X coordinate of the scanned area.'),
  }),
});

export type ExtractedMetadataType = z.infer<typeof ExtractedMetadataSchema>;
